import { api } from '@/api';

const COOKIE_NAME = 'anonymous_id';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;
const REGISTERED_STORAGE_KEY = 'anonymous_visitor_registered';
const REGISTER_INTERVAL_MS = 1000 * 60 * 60 * 24;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const getCookie = (name) => {
  const cookie = document.cookie.split('; ').find((item) => item.startsWith(`${name}=`));

  return cookie ? decodeURIComponent(cookie.split('=').slice(1).join('=')) : null;
};

const setCookie = (name, value) => {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';

  document.cookie = `${name}=${encodeURIComponent(
    value,
  )}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
};

const randomHexDigits = (count) => {
  const crypto = window.crypto || window.msCrypto;

  if (crypto?.getRandomValues) {
    const bytes = crypto.getRandomValues(new Uint8Array(count));

    return Array.from(bytes, (byte) => (byte % 16).toString(16));
  }

  return Array.from({ length: count }, () => Math.floor(Math.random() * 16).toString(16));
};

const createUuid = () => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  const digits = randomHexDigits(30);

  return [
    digits.slice(0, 8).join(''),
    digits.slice(8, 12).join(''),
    `4${digits.slice(12, 15).join('')}`,
    `8${digits.slice(15, 18).join('')}`,
    digits.slice(18, 30).join(''),
  ].join('-');
};

const readRegistration = () => {
  try {
    return JSON.parse(window.localStorage.getItem(REGISTERED_STORAGE_KEY));
  } catch (e) {
    return null;
  }
};

const writeRegistration = (id) => {
  try {
    window.localStorage.setItem(REGISTERED_STORAGE_KEY, JSON.stringify({ id, ts: Date.now() }));
    return true;
  } catch (e) {
    return false;
  }
};

const readAnonymousId = () => {
  const anonymousId = getCookie(COOKIE_NAME);

  return anonymousId && UUID_PATTERN.test(anonymousId) ? anonymousId : null;
};

const issueAnonymousId = () => {
  const anonymousId = createUuid();
  setCookie(COOKIE_NAME, anonymousId);

  return anonymousId;
};

export const getAnonymousId = () => readAnonymousId() ?? issueAnonymousId();

const getUtmParamsFromLocation = () => {
  const params = new URLSearchParams(window.location.search);
  const utm = {};

  params.forEach((value, key) => {
    if (key.startsWith('utm_')) {
      utm[key] = value;
    }
  });

  return utm;
};

const isRegisteredRecently = (anonymousId) => {
  const registration = readRegistration();

  return registration?.id === anonymousId
    && Date.now() - registration.ts < REGISTER_INTERVAL_MS
    && Date.now() >= registration.ts;
};

const makeVisitPayload = (anonymousId, utm) => ({
  id: anonymousId,
  type: 'anonymous',
  ...utm,
  landing_page_url: window.location.href,
  referrer_url: document.referrer || null,
});

export const appendVisitorToFormData = (formData) => {
  try {
    formData.append('visitor_id', getAnonymousId());
    formData.append('visitor_type', 'anonymous');
    return true;
  } catch (e) {
    return false;
  }
};

export const registerAnonymousVisitor = async () => {
  const anonymousId = getAnonymousId();
  const utm = getUtmParamsFromLocation();
  const hasUtm = Object.keys(utm).length > 0;

  if (!hasUtm && isRegisteredRecently(anonymousId)) {
    return;
  }

  await api.createVisitor(makeVisitPayload(anonymousId, utm));
  writeRegistration(anonymousId);
};
